import { StandardEvents } from '@shopify/events';

/**
 * Switches the per-variant panels (`[data-thcc-variant]`) when the shopper selects a variant.
 *
 * Listens to `shopify:product:select` on `document`. The event is dispatched by `variant-picker`
 * (assets/variant-picker.js, `fetchUpdatedSection`) and carries `event.product.id`,
 * `event.detail.variantId` (synchronous) and `event.promise`, which resolves with
 * `{ variant, detail: { resource } }` (see assets/product-price.js for another consumer).
 * Only panels whose closest `[data-thcc-product]` ancestor matches the product id are touched.
 */

/**
 * Extracts the numeric id from a plain id or a `gid://shopify/...` string.
 * @param {unknown} value
 * @returns {string}
 */
function numericId(value) {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return text.slice(text.lastIndexOf('/') + 1);
}

/**
 * @param {string} productId
 * @param {string} variantId
 */
function showVariant(productId, variantId) {
  if (!productId || !variantId) return;

  for (const panel of document.querySelectorAll('[data-thcc-variant]')) {
    const owner = panel.closest('[data-thcc-product]');
    if (!(owner instanceof HTMLElement) || owner.dataset.thccProduct !== productId) continue;
    if (!(panel instanceof HTMLElement)) continue;

    panel.hidden = panel.dataset.thccVariant !== variantId;
  }
}

/** @param {import('@shopify/events').ProductSelectEvent} event */
async function handleProductSelect(event) {
  // Product cards (quick add / swatches) are not part of the product page.
  if (event.target instanceof Element && event.target.closest('product-card')) return;

  const productId = numericId(event.product?.id);
  const syncVariantId = numericId(event.detail?.variantId);

  // Fast path: the picker already knows the chosen variant.
  if (syncVariantId) showVariant(productId, syncVariantId);

  try {
    const result = await event.promise;
    const resolvedId = numericId(result?.detail?.resource?.id ?? result?.variant?.id);
    if (resolvedId && resolvedId !== syncVariantId) showVariant(productId, resolvedId);
  } catch (error) {
    // A newer selection aborts the previous request; keep whatever the fast path showed.
    if (/** @type {Error} */ (error)?.name !== 'AbortError') {
      console.warn('[thcc-variant-panels] Event promise rejected:', error);
    }
  }
}

document.addEventListener(StandardEvents.productSelect, handleProductSelect);
